import { PRICE_BASIS } from "routes/product/pricing/pricing";

/**
 * Special invoice line types. They share the Product select with catalog
 * products, so each is stored in `product_id` under a string value and sent to
 * the API as a boolean flag with a null product.
 */
export const SPECIAL_LINE_TYPES = [
  {
    value: "total_value_override",
    label: "Total Value Override",
    flag: "is_total_value_override",
  },
  { value: "penalty", label: "Penalty", flag: "is_penalty" },
  { value: "discount", label: "Discount", flag: "is_discount" },
];

export const isSpecialLineValue = value =>
  SPECIAL_LINE_TYPES.some(type => type.value === value);

export const isSpecialLine = line =>
  Boolean(line.is_discount || line.is_penalty || line.is_total_value_override);

/** The three type flags implied by a Product select value. */
export const lineFlagsFor = productValue =>
  Object.fromEntries(
    SPECIAL_LINE_TYPES.map(type => [type.flag, type.value === productValue])
  );

const toInputValue = value => (value == null ? "" : String(value));

export const emptyInvoiceLine = () => ({
  product_id: null,
  product_price_id: null,
  price_basis: PRICE_BASIS.PER_YEAR,
  amount: "",
  term: "",
  quantity: "",
  license_start_date: null,
  license_end_date: null,
  ...lineFlagsFor(null),
});

/** An API invoice line as form values. */
export const toFormInvoiceLine = invoice => {
  const special = SPECIAL_LINE_TYPES.find(type => invoice[type.flag]);
  const line = {
    id: invoice.id,
    product_id: special ? special.value : invoice.product_id,
    product_price_id: invoice.product_price_id ?? null,
    price_basis: invoice.price_basis ?? PRICE_BASIS.PER_YEAR,
    amount: toInputValue(invoice.amount),
    term: toInputValue(invoice.term),
    quantity: toInputValue(invoice.quantity),
    license_start_date: invoice.license_start_date ?? null,
    license_end_date: invoice.license_end_date ?? null,
    ...lineFlagsFor(special?.value ?? null),
  };
  // A catalog line saved without a term or quantity counts them as 1.
  if (!special && line.product_id) {
    line.term = line.term || "1";
    line.quantity = line.quantity || "1";
  }
  return line;
};

const toInteger = value => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

/** A form invoice line as the API payload. */
export const toPayloadInvoiceLine = line => {
  const special = isSpecialLine(line);
  return {
    ...(line.id != null && { id: line.id }),
    product_id: special ? null : line.product_id,
    product_price_id: special ? null : (line.product_price_id ?? null),
    amount: parseFloat(line.amount),
    term: special ? null : toInteger(line.term),
    quantity: special ? null : toInteger(line.quantity),
    license_start_date: line.license_start_date || null,
    license_end_date: line.license_end_date || null,
    is_discount: Boolean(line.is_discount),
    is_penalty: Boolean(line.is_penalty),
    is_total_value_override: Boolean(line.is_total_value_override),
  };
};
